import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { GET_AUDITS } from "../../../../apis/settingsApis/audit-apis/auditApis";
import { GET_FRAMEWORK_LIST } from "../../../../apis/settingsApis/framework-apis/frameworkApis";
import { formatDate } from "../../../../utils/dateUtils";
import { AuditStatus, AuditStatusLabel } from "../../../../utils/enums";

export const useAuditList = ({ filterAuditType, filterFramework }) => {
  // Build filter object for API
  const apiFilter = useMemo(() => {
    const filter = {};
    
    if (filterAuditType) {
      filter.audit_type = filterAuditType;
    }
    if (filterFramework) {
      filter.framework_id = parseInt(filterFramework);
    }
    return filter;
  }, [filterAuditType, filterFramework]);

  const { data: auditsData, loading, refetch } = useQuery(GET_AUDITS, {
    variables: {
      page: 1,
      limit: 1000,
      filter: apiFilter,
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: frameworksData } = useQuery(GET_FRAMEWORK_LIST, {
    fetchPolicy: "cache-first",
  });

  const audits = auditsData?.getAudits?.data || [];
  const frameworks = frameworksData?.getFramework || [];

  const processedAudits = useMemo(() => {
    return audits.map((audit) => {
      const framework = frameworks.find((f) => f.framework_id === audit.framework_id);

      // auditors comes directly from API with name/email
      const auditors = audit.auditors || [];
      const auditorNames = auditors.map((a) => a.name?.trim()).filter(Boolean);

      let auditorName = "";
      if (auditorNames.length === 1) {
        auditorName = auditorNames[0];
      } else if (auditorNames.length > 1) {
        auditorName = `${auditorNames[0]} +${auditorNames.length - 1}`;
      } else if (audit.audit_type === "internal") {
        auditorName = "Internal Auditing";
      } else {
        auditorName = "Not Assigned";
      }
      
      const statusKey = audit.audit_status || AuditStatus.ACTIVE;
      
      const evidencePeriod = audit.audit_start_date && audit.audit_end_date
        ? `${formatDate(audit.audit_start_date)} - ${formatDate(audit.audit_end_date)}`
        : "N/A";
      
      const frameworkDisplay =
        (framework?.framework_name && String(framework.framework_name).trim()) ||
        framework?.framework_description ||
        "N/A";
      
      return {
        ...audit,
        audit_name: audit.audit_name || "-",
        framework_name: frameworkDisplay,
        auditor_name: auditorName,
        auditor_names_list: auditorNames,
        customer_name: audit.customer_name || "N/A",
        evidence_period: evidencePeriod,
        total_controls: audit.total_controls || 0,
        audit_status: statusKey,
        status_label: AuditStatusLabel[statusKey] || "Active",
      };
    });
  }, [audits, frameworks]);

  return {
    audits,
    frameworks,
    processedAudits,
    loading,
    refetch,
  };
};
